import { Clock, Download, Eraser, FileText, PanelTop, RefreshCw, Search, Smartphone, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, SectionCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatDateTimeValue } from '@/lib/utils';

import { useArchivosLog, useContenidoLog, useMutarLogs } from '../configuracion.api';
import { LogDetalleDialogo } from '../components/log-detalle-dialogo';
import { COLOR_NIVEL, ETIQUETA_NIVEL, PUNTO_NIVEL, formatearBytes, formatearLineas } from '../lib/configuracion-format';
import { descargarArchivoLog } from '../lib/descargar-log';
import type { LineaLog, NivelLog } from '../configuracion.types';

const NIVELES: (NivelLog | 'todos')[] = ['todos', 'trace', 'debug', 'info', 'warn', 'error', 'fatal'];

const OPCIONES_LINEAS = [100, 300, 1000, 2000];

/** Fuente que se muestra al entrar (backend del panel web). */
const ORIGEN_POR_DEFECTO = 'api';

const SELECT_CLASSES =
  'h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 transition-colors ' +
  'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mensaje de error legible para las mutaciones de logs. */
function mensajeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'No se pudo completar la operación sobre el log.';
}

/** Icono del componente dueño de la fuente de logs. */
function IconoFuente({ componente }: { componente: string }) {
  if (componente === 'app') return <Smartphone className="size-4 shrink-0 text-ink-400" aria-hidden="true" />;

  return <PanelTop className="size-4 shrink-0 text-ink-400" aria-hidden="true" />;
}

/**
 * Logs del Sistema (`/configuracion/logs`).
 *
 * El **panel web** y la **app** son componentes distintos, cada uno con su propio
 * backend y sus propios logs, así que la pantalla muestra los dos y se elige la
 * fuente con el selector de componente (parámetro `origen` de la API).
 */
export function LogsPage() {
  useDocumentTitle('Configuración · Logs del sistema');

  const [origen, setOrigen] = useState(ORIGEN_POR_DEFECTO);
  const [seleccion, setSeleccion] = useState<string | null>(null);
  const [nivel, setNivel] = useState<NivelLog | 'todos'>('todos');
  const [buscar, setBuscar] = useState('');
  const [logger, setLogger] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [lineas, setLineas] = useState(300);
  const [enVivo, setEnVivo] = useState(false);
  const [filtroArchivo, setFiltroArchivo] = useState('');
  /** Línea abierta en el detalle (null = sin diálogo). */
  const [detalle, setDetalle] = useState<LineaLog | null>(null);
  const [porVaciar, setPorVaciar] = useState<string | null>(null);
  const [porEliminar, setPorEliminar] = useState<string | null>(null);
  const [limpiarTodo, setLimpiarTodo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const consolaRef = useRef<HTMLDivElement>(null);
  /** Se desactiva cuando el usuario sube a leer: así el refresco no lo arrastra. */
  const seguirAlFinal = useRef(true);

  const listado = useArchivosLog(origen);
  const mutaciones = useMutarLogs();

  const archivos = listado.data?.archivos ?? [];
  const fuentes = listado.data?.fuentes ?? [];

  /** Cambia de componente: se limpia la selección (el archivo es de otra carpeta). */
  const cambiarOrigen = (id: string) => {
    setOrigen(id);
    setSeleccion(null);
    setLogger('');
    setAviso(null);
    seguirAlFinal.current = true;
  };

  /**
   * Archivo mostrado: el elegido por el usuario o, si ya no existe (se eliminó,
   * o el listado aún no se refrescó), el más reciente.
   */
  const archivo = useMemo(() => {
    const lista = listado.data?.archivos ?? [];

    if (seleccion && lista.some((item) => item.nombre === seleccion)) return seleccion;

    return lista[0]?.nombre ?? null;
  }, [seleccion, listado.data]);

  const filtros = useMemo(
    () => ({ origen, archivo, nivel, buscar, logger, desde, hasta, lineas }),
    [origen, archivo, nivel, buscar, logger, desde, hasta, lineas],
  );

  const contenido = useContenidoLog(filtros, enVivo, Boolean(archivo));

  /** Un archivo comprimido (.gz) no se puede vaciar: sólo eliminar. */
  const comprimido = contenido.data?.comprimido === true;

  /** Loggers del archivo leído (vacío en los del panel: no usan `logger_name`). */
  const loggers = contenido.data?.loggers ?? [];

  const rangoHoras = contenido.data?.filtro_horas ?? null;
  const hayRangoHoras = Boolean(rangoHoras?.desde || rangoHoras?.hasta);

  /** Descripción del rango aplicado, para el resumen de la consola. */
  const descripcionRango = hayRangoHoras
    ? ` entre ${rangoHoras?.desde ?? '00:00:00'} y ${rangoHoras?.hasta ?? '23:59:59'}` +
      (rangoHoras?.cruza_medianoche ? ' (cruza medianoche)' : '')
    : '';

  /** `true` cuando hay algún filtro puesto (para ofrecer limpiarlos de una vez). */
  const hayFiltros =
    nivel !== 'todos' || buscar !== '' || logger !== '' || desde !== '' || hasta !== '';

  /** Quita todos los filtros de la consola (no toca la búsqueda de archivos). */
  const limpiarFiltros = () => {
    setNivel('todos');
    setBuscar('');
    setLogger('');
    setDesde('');
    setHasta('');
  };

  // La consola arranca al final (lo más reciente), como un `tail`, y sigue ahí
  // mientras el usuario no se desplace hacia arriba.
  useEffect(() => {
    const nodo = consolaRef.current;

    if (nodo && seguirAlFinal.current) nodo.scrollTop = nodo.scrollHeight;
  }, [archivo, nivel, buscar, logger, desde, hasta, lineas, contenido.data]);

  const visibles = archivos.filter((item) =>
    item.nombre.toLowerCase().includes(filtroArchivo.trim().toLowerCase()),
  );

  const descargar = async (nombre: string) => {
    setAviso(null);

    try {
      await descargarArchivoLog(origen, nombre);
    } catch (error) {
      setAviso(error instanceof Error ? error.message : 'No se pudo descargar el log.');
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <FileText className="size-6 text-brand-500" aria-hidden="true" />
            Logs del sistema
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            El panel web y la app son componentes distintos y cada uno escribe sus propios logs:
            elige el componente para ver los de su backend. Los archivos que un proceso mantiene
            abiertos se pueden vaciar, pero no eliminar: borrarlos haría que se pierda lo que se siga
            escribiendo.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-medium text-ink-600">
            <input
              type="checkbox"
              checked={enVivo}
              onChange={(event) => setEnVivo(event.target.checked)}
              className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-200"
            />
            En vivo (5 s)
          </label>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void listado.refetch()}
            loading={listado.isFetching}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Actualizar
          </Button>

          <Button variant="danger" size="sm" onClick={() => setLimpiarTodo(true)}>
            <Eraser className="size-3.5" aria-hidden="true" />
            Limpiar todo
          </Button>
        </div>
      </header>

      <div className="rounded-card border border-ink-100 bg-white p-3">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
          Componente
        </p>

        <div className="flex flex-wrap gap-2">
          {(fuentes.length > 0
            ? fuentes
            : [
                {
                  id: ORIGEN_POR_DEFECTO,
                  componente: 'panel',
                  etiqueta: 'Panel web',
                  descripcion: 'Backend del panel (edsuite-api).',
                  carpeta: '',
                  extensiones: ['.log'],
                  disponible: true,
                },
              ]
          ).map((fuente) => (
            <button
              key={fuente.id}
              type="button"
              onClick={() => cambiarOrigen(fuente.id)}
              aria-pressed={fuente.id === origen}
              className={cn(
                'flex min-w-[16rem] flex-1 items-start gap-2.5 rounded-control border px-3 py-2.5 text-left transition-colors',
                fuente.id === origen
                  ? 'border-brand-300 bg-brand-50'
                  : 'border-ink-100 bg-white hover:border-brand-200 hover:bg-brand-50/40',
              )}
            >
              <IconoFuente componente={fuente.componente} />

              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-ink-900">{fuente.etiqueta}</span>
                  {!fuente.disponible ? <Badge variant="warning">Sin acceso</Badge> : null}
                </span>

                <span className="mt-0.5 block text-[11px] text-ink-500">{fuente.descripcion}</span>

                {fuente.carpeta ? (
                  <span
                    className="mt-0.5 block truncate font-mono text-[10px] text-ink-400"
                    title={fuente.carpeta}
                  >
                    {fuente.carpeta}
                  </span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      </div>

      {aviso ? (
        <Alert variant="error" title="No se pudo descargar el log">
          {aviso}
        </Alert>
      ) : null}

      {mutaciones.limpiarTodos.isError ? (
        <Alert variant="error" title="No se pudieron limpiar los logs">
          {mensajeError(mutaciones.limpiarTodos.error)}
        </Alert>
      ) : null}

      {mutaciones.limpiarTodos.isSuccess ? (
        <Alert variant="success" title="Logs limpiados">
          {mutaciones.limpiarTodos.data.mensaje}
          {mutaciones.limpiarTodos.data.omitidos.length > 0
            ? ` No se pudieron tocar: ${mutaciones.limpiarTodos.data.omitidos.join(', ')}.`
            : ''}
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <SectionCard
          title="Archivos"
          description={listado.data ? formatearBytes(archivos.reduce((total, item) => total + item.bytes, 0)) + ' en total' : undefined}
        >
          {listado.data && !listado.data.disponible ? (
            <Alert variant="error" title="No se puede leer la carpeta de logs">
              La carpeta <span className="font-mono">{listado.data.carpeta}</span> no existe o el
              usuario que ejecuta EDSuite API no tiene permiso para leerla.
            </Alert>
          ) : null}

          <Input
            leftIcon={<Search className="size-4" aria-hidden="true" />}
            placeholder="Buscar archivo…"
            value={filtroArchivo}
            onChange={(event) => setFiltroArchivo(event.target.value)}
          />

          <div className="mt-3 space-y-1.5">
            {listado.isPending ? (
              <Skeleton className="h-40 rounded-card" />
            ) : listado.isError ? (
              <Alert variant="error" title="No se pudo leer la carpeta de logs">
                {mensajeError(listado.error)}
              </Alert>
            ) : visibles.length === 0 ? (
              <p className="rounded-control border border-dashed border-ink-200 px-3 py-8 text-center text-sm text-ink-500">
                No hay archivos de log.
              </p>
            ) : (
              visibles.map((item) => (
                <button
                  key={item.nombre}
                  type="button"
                  onClick={() => {
                    setSeleccion(item.nombre);
                    // Los logger_name son de cada archivo: no se arrastran.
                    setLogger('');
                  }}
                  aria-current={item.nombre === archivo ? 'true' : undefined}
                  className={cn(
                    'w-full rounded-control border px-3 py-2 text-left transition-colors',
                    item.nombre === archivo
                      ? 'border-brand-300 bg-brand-50'
                      : 'border-ink-100 bg-white hover:border-brand-200 hover:bg-brand-50/40',
                  )}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate font-mono text-xs font-semibold text-ink-800">
                      {item.nombre}
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      {item.comprimido ? <Badge variant="neutral">gz</Badge> : null}
                      {item.en_uso ? <Badge variant="warning">En uso</Badge> : null}
                    </span>
                  </span>

                  <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-ink-500">
                    <span>{formatearBytes(item.bytes)}</span>
                    <span>{formatearLineas(item.lineas)} líneas</span>
                    <span>{formatDateTimeValue(item.modificado)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </SectionCard>

        <Card className="flex flex-col overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 px-5 py-4">
            <div className="min-w-0">
              <h3 className="truncate font-mono text-sm font-semibold text-ink-900">
                {archivo ?? 'Sin archivo seleccionado'}
              </h3>
              <p className="mt-0.5 text-xs text-ink-500">
                {contenido.data
                  ? `${formatearLineas(contenido.data.total_lineas)} líneas leídas · ${formatearBytes(contenido.data.bytes)}`
                  : 'Selecciona un archivo de la izquierda.'}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!archivo}
                onClick={() => {
                  if (archivo) void descargar(archivo);
                }}
              >
                <Download className="size-3.5" aria-hidden="true" />
                Descargar
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!archivo || comprimido}
                title={
                  comprimido
                    ? 'Un archivo comprimido (.gz) no se puede vaciar'
                    : 'Borrar el contenido del archivo'
                }
                onClick={() => setPorVaciar(archivo)}
              >
                <Eraser className="size-3.5" aria-hidden="true" />
                Vaciar
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!archivo || contenido.data?.en_uso === true}
                title={
                  contenido.data?.en_uso
                    ? 'El archivo está en uso: sólo se puede vaciar'
                    : 'Eliminar el archivo'
                }
                onClick={() => setPorEliminar(archivo)}
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
                Eliminar
              </Button>
            </div>
          </div>

          {/* Filtros en tres filas, cada una con un propósito: 1) nivel, que es lo
              que más se usa; 2) texto y cuánto leer; 3) logger y rango horario.
              Así no se mezclan controles y nada queda suelto al angostarse. */}
          <div className="space-y-3 border-b border-ink-100 px-5 py-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {NIVELES.map((opcion) => (
                <button
                  key={opcion}
                  type="button"
                  onClick={() => setNivel(opcion)}
                  aria-pressed={nivel === opcion}
                  title={opcion === 'todos' ? 'Todos los niveles' : `Nivel ${opcion}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                    nivel === opcion
                      ? 'border-ink-900 bg-ink-900 text-white'
                      : 'border-ink-200 bg-white text-ink-600 hover:border-brand-300 hover:text-brand-700',
                  )}
                >
                  {opcion !== 'todos' ? (
                    <span className={cn('size-2 rounded-full', PUNTO_NIVEL[opcion])} aria-hidden="true" />
                  ) : null}
                  {opcion === 'todos' ? 'Todos' : ETIQUETA_NIVEL[opcion]}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[10rem] flex-1">
                <Input
                  leftIcon={<Search className="size-4" aria-hidden="true" />}
                  placeholder="Filtrar líneas…"
                  value={buscar}
                  onChange={(event) => setBuscar(event.target.value)}
                />
              </div>

              <label className="flex shrink-0 items-center gap-2 text-xs text-ink-500">
                Últimas
                <Select
                  value={lineas}
                  onChange={(event) => setLineas(Number(event.target.value))}
                  className={SELECT_CLASSES}
                >
                  {OPCIONES_LINEAS.map((opcion) => (
                    <option key={opcion} value={opcion}>
                      {opcion}
                    </option>
                  ))}
                </Select>
                líneas
              </label>
            </div>

            <div className="flex flex-wrap items-end gap-2">
              {/* Sólo aparece en los logs de la app: los del panel no usan `logger_name`. */}
              {loggers.length > 0 ? (
                <span className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-ink-700">Logger</span>
                  <Select
                    value={logger}
                    onChange={(event) => setLogger(event.target.value)}
                    searchable={loggers.length > 8}
                    title={logger || 'Todos los loggers'}
                    className={cn(SELECT_CLASSES, 'h-12 w-[13rem] max-w-full')}
                  >
                    <option value="">Todos los loggers</option>
                    {loggers.map((item) => (
                      <option key={item.nombre} value={item.nombre}>
                        {`${item.nombre} (${formatearLineas(item.total)})`}
                      </option>
                    ))}
                  </Select>
                </span>
              ) : null}

              {/* «Hora desde/hasta»: la hora local del servidor, la misma que muestra
                  el canal de la izquierda de cada línea. */}
              <Input
                type="time"
                label="Hora desde"
                containerClassName="w-[7rem]"
                value={desde}
                onChange={(event) => setDesde(event.target.value)}
              />
              <Input
                type="time"
                label="Hora hasta"
                containerClassName="w-[7rem]"
                value={hasta}
                onChange={(event) => setHasta(event.target.value)}
              />

              {hayFiltros ? (
                <Button variant="ghost" size="sm" className="mb-1" onClick={limpiarFiltros}>
                  <X className="size-3.5" aria-hidden="true" />
                  Limpiar filtros
                </Button>
              ) : null}
            </div>

            {rangoHoras?.cruza_medianoche ? (
              <p className="flex items-center gap-1.5 text-[11px] text-amber-600">
                <Clock className="size-3.5" aria-hidden="true" />
                El rango cruza la medianoche (turno nocturno)
              </p>
            ) : null}
          </div>

          {contenido.data && contenido.data.coincidencias > 0 ? (
            <p className="border-b border-ink-100 px-5 py-2 text-xs text-ink-500">
              Mostrando {contenido.data.desde}–{contenido.data.hasta} de{' '}
              {formatearLineas(contenido.data.coincidencias)} línea(s) que cumplen el filtro
              {descripcionRango}
              {contenido.data.truncado ? ' · sólo se leyó el final del archivo' : ''}
              {contenido.data.sin_hora > 0
                ? ` · ${formatearLineas(contenido.data.sin_hora)} sin marca de tiempo quedaron fuera`
                : ''}
              {' · '}clic en una línea para verla formateada
            </p>
          ) : null}

          <div
            ref={consolaRef}
            onScroll={(event) => {
              const nodo = event.currentTarget;
              seguirAlFinal.current =
                nodo.scrollHeight - nodo.scrollTop - nodo.clientHeight < 40;
            }}
            className="min-h-[28rem] flex-1 overflow-auto bg-ink-950 px-4 py-3 font-mono text-[12px] leading-relaxed"
          >
            {contenido.isPending ? (
              <p className="text-ink-400">Leyendo…</p>
            ) : contenido.isError ? (
              <p className="text-rose-400">{mensajeError(contenido.error)}</p>
            ) : !archivo ? (
              <p className="text-ink-400">Selecciona un archivo para ver su contenido.</p>
            ) : (contenido.data?.lineas.length ?? 0) === 0 ? (
              <p className="text-ink-400">
                {contenido.data?.total_lineas === 0
                  ? 'El archivo está vacío.'
                  : 'Ninguna línea coincide con el filtro.'}
              </p>
            ) : (
              contenido.data?.lineas.map((linea) => (
                <button
                  key={linea.numero}
                  type="button"
                  onClick={() => setDetalle(linea)}
                  title="Ver la línea formateada"
                  aria-label={`Ver formateada la línea ${linea.numero}`}
                  className="flex w-full gap-3 rounded px-1 text-left transition-colors hover:bg-white/[0.07] focus-visible:bg-white/[0.09] focus-visible:outline-none"
                >
                  <span className="w-12 shrink-0 select-none text-right text-ink-600">
                    {linea.numero}
                  </span>
                  {/* Hora local del registro: en los logs de la app la del JSON viene en UTC. */}
                  <span className="w-[4.5rem] shrink-0 text-ink-500">{linea.hora ?? ''}</span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 whitespace-pre-wrap break-words',
                      COLOR_NIVEL[linea.nivel],
                    )}
                  >
                    {linea.texto}
                  </span>
                </button>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Detalle de la línea pulsada: se monta condicionalmente (con `key`) para
          que el estado del diálogo arranque limpio en cada línea. */}
      {detalle ? (
        <LogDetalleDialogo
          key={detalle.numero}
          linea={detalle}
          archivo={archivo}
          onClose={() => setDetalle(null)}
        />
      ) : null}

      <ConfirmDialog
        open={porVaciar !== null}
        title={`Vaciar ${porVaciar ?? ''}`}
        description="Se borra todo el contenido del archivo. El registro se pierde y no se puede recuperar."
        confirmLabel="Sí, vaciar"
        loading={mutaciones.vaciar.isPending}
        onConfirm={() => {
          if (!porVaciar) return;

          mutaciones.vaciar.mutate(
            { origen, archivo: porVaciar },
            { onSettled: () => setPorVaciar(null) },
          );
        }}
        onClose={() => setPorVaciar(null)}
      />

      <ConfirmDialog
        open={porEliminar !== null}
        title={`Eliminar ${porEliminar ?? ''}`}
        description="El archivo se borra del servidor. Esta acción no se puede deshacer."
        confirmLabel="Sí, eliminar"
        loading={mutaciones.eliminar.isPending}
        onConfirm={() => {
          if (!porEliminar) return;

          const nombre = porEliminar;

          mutaciones.eliminar.mutate(
            { origen, archivo: nombre },
            {
              // Se limpia la selección antes de que se refresque el listado, para
              // no volver a pedir un archivo que ya no está.
              onSuccess: () => setSeleccion(null),
              onSettled: () => setPorEliminar(null),
            },
          );
        }}
        onClose={() => setPorEliminar(null)}
      />

      <ConfirmDialog
        open={limpiarTodo}
        title={`Limpiar los logs de ${listado.data?.etiqueta ?? 'la fuente'}`}
        description="Los archivos en uso se vacían y el resto se elimina. El registro se pierde y no se puede recuperar."
        confirmLabel="Sí, limpiar todo"
        loading={mutaciones.limpiarTodos.isPending}
        onConfirm={() => {
          mutaciones.limpiarTodos.mutate(origen, { onSettled: () => setLimpiarTodo(false) });
        }}
        onClose={() => setLimpiarTodo(false)}
      />
    </div>
  );
}
